// Pure draw-helper functions for the place-selection draw flow.
//
// These are intentionally free of any Vue/component or MapboxDraw dependency so
// that fast-check property tests can import and exercise them directly. They
// mirror the extraction pattern used by placeEditModalHelpers.js.
// Feature: place-selection-draw-toolbar (Task 2.1)

/**
 * Plain, non-reactive deep copy via structural JSON round-trip.
 *
 * Mirrors PlaceEditModal.cloneJSON: prevents handing Vue proxies to MapboxDraw
 * (which deep-traverses internally and can recurse infinitely). Returns null
 * for null/undefined input.
 *
 * @param {*} value - The value to deep-clone.
 * @returns {*} A structural deep clone, or null when the input is null/undefined.
 */
export function cloneJSON(value) {
  return value == null ? null : JSON.parse(JSON.stringify(value));
}

/**
 * Build the confirmation message for a delete action.
 *
 * When one or more shapes are selected, the message names the "selected" shapes
 * and includes the selected count. When nothing is selected, the message names
 * "all" shapes and includes the total count.
 *
 * @param {number} selectedCount - Number of currently selected shapes.
 * @param {number} totalCount - Total number of drawn shapes.
 * @returns {string} The confirmation dialog message.
 */
export function buildDeleteMessage(selectedCount, totalCount) {
  if (selectedCount > 0) {
    const noun = selectedCount === 1 ? "shape" : "shapes";
    return `Are you sure you wish to delete the ${selectedCount} selected ${noun}?`;
  }
  const noun = totalCount === 1 ? "shape" : "shapes";
  return `Are you sure you wish to delete all ${totalCount} ${noun}?`;
}

/**
 * Determine which contextual banner should be visible from the drawing state
 * and the current shape count.
 *
 * - `info` is true if and only if the map is in Drawing_Mode.
 * - `warning` is true if and only if the map is not drawing and there are no
 *   shapes.
 * - Neither is shown when not drawing and at least one shape exists.
 *
 * @param {boolean} isDrawing - Whether the map is actively drawing a polygon.
 * @param {number} count - Number of drawn shapes.
 * @returns {{ info: boolean, warning: boolean }} Banner visibility flags.
 */
export function bannerVisibility(isDrawing, count) {
  return {
    info: !!isDrawing,
    warning: !isDrawing && count === 0,
  };
}

/**
 * Determine whether a place name is blank (null/undefined or whitespace-only).
 *
 * @param {string} name - The candidate place name.
 * @returns {boolean} True when the name is missing or contains only whitespace.
 */
export function isBlankName(name) {
  return name == null || String(name).trim() === "";
}

/**
 * Merge an array of Polygon/MultiPolygon features into a single MultiPolygon
 * geometry. Each Polygon contributes one entry to the resulting `coordinates`
 * array; each MultiPolygon contributes each of its constituent polygons. The
 * length of the resulting `coordinates` therefore equals the total number of
 * constituent polygons across all input features.
 *
 * Reuses the merge logic from the current Add Place path in
 * PlaceSelectionDrawTab.vue.
 *
 * @param {Array<Object>} features - Array of GeoJSON Polygon/MultiPolygon features.
 * @returns {{ type: string, coordinates: Array }} A MultiPolygon geometry.
 */
export function mergeShapesToMultiPolygon(features) {
  const shapes = Array.isArray(features) ? features : [];
  const coordinates = [];
  for (const shape of shapes) {
    const g = shape && shape.geometry;
    if (!g) continue;
    if (g.type === "Polygon" && Array.isArray(g.coordinates)) {
      coordinates.push(g.coordinates);
    } else if (g.type === "MultiPolygon" && Array.isArray(g.coordinates)) {
      coordinates.push(...g.coordinates);
    }
  }
  return { type: "MultiPolygon", coordinates };
}
